// Presets ("scenes") — each reconfigures the whole experience: which excitations are
// used, the generative vibe (via gardening-dial biases + dominant mood), global decay,
// and reverb depth. Applied at the app layer so the generative core stays untouched.
import type { Mood } from '@shared/parameterState';
import type { MultihitConfig } from './multihit';

export interface Preset {
  id: string;
  name: string;
  blurb: string;
  /** dial biases applied to the generative engine (0..1 each). */
  dials: { calmActive: number; darkBright: number; pureRough: number; roaming: number };
  /** dominant mood to bias toward on selection. */
  mood: Mood;
  /** relative weights for picking each strike's excitation. `sampler` defaults to 0 if omitted. */
  excitation: { strike: number; bow: number; grain: number; sampler?: number };
  /** multiplier on nominal decay time. */
  decayScale: number;
  /** added to the generative `space` (reverb depth) baseline, clamped to [0,1]. */
  reverbBias: number;
  /** seconds a bowed note is held before release [min,max]. */
  bowHold: [number, number];
  /** octave shift applied to playable/struck pitches (negative = lower). */
  octaveShift: number;
  /** drift motion character: speed (time mult) and amplitude (roam distance mult). */
  motion: { speed: number; amp: number };
  /** optional pitch scale (key into generative SCALES); default 'just'. */
  scale?: string;
  /** optional sonic base overriding the body-class spectrum (gamelan/glass/sub/organ/fm). */
  ratioFamily?: string;
  /** optional multihit pattern spawned by each struck event (rhythm). */
  multihit?: MultihitConfig;
  /** optional echo: wet 0..1, time s, feedback 0..0.85. */
  delay?: { wet: number; timeSec: number; feedback: number };
  /** optional spatial pitch gradient 0..1: top of screen higher, bottom lower. */
  zoning?: number;
  /** opt this scene into device-tilt gravity when motion is available (mobile). Default off. */
  gravity?: boolean;
  /** master "drive" into the saturator/limiter (scales makeup gain). 1 = default; <1 = cleaner. */
  drive?: number;
  /**
   * Sound bank to auto-select when this preset is applied (a `SoundBank.id`, or `NO_BANK`). Grain/
   * sampler presets set this so they sound as designed out of the box; presets that omit it reset
   * Excite to None (noise strikes), so the Excite control tracks the preset's intent.
   */
  excitationBank?: string;
}

/**
 * Per-preset master drive starting points (measured: most presets ran 10–17 dB into the limiter +
 * soft-clip = "overdriven"). These create a deliberate spread — energetic scenes keep some grit,
 * pretty/calm scenes breathe — and are tunable live via the Drive control. Absent id => 1.0.
 */
// With polyphony-aware makeup (master auto-trims by active-voice count), the old per-preset drive
// reductions are no longer needed to fight density — polyGain does that. Drive is now just a light
// taste knob: most scenes sit at 0.85 (full + clean even at max polyphony, measured), while sparse
// or intentionally-gritty scenes push to unity.
const PRESET_DRIVE: Record<string, number> = {
  // energetic scenes — let them touch the soft-clip for a little grit
  'storm-glass': 1, current: 1, maelstrom: 1, 'gamelan-reef': 1,
  // naturally sparse — unity keeps them present
  'glass-chimes': 1, 'tidal-pulse': 1, sonar: 1,
};

/** Effective master drive for a preset (explicit `drive` field wins, else 0.85 default). */
export function presetDrive(p: Preset): number {
  return p.drive ?? PRESET_DRIVE[p.id] ?? 0.85;
}

export const PRESETS: Preset[] = [
  {
    id: 'glass-chimes',
    name: 'Glass Chimes',
    blurb: 'bright struck silica — the classic',
    dials: { calmActive: 0.45, darkBright: 0.62, pureRough: 0.35, roaming: 0.4 },
    mood: 'tidepool',
    excitation: { strike: 1, bow: 0, grain: 0 },
    decayScale: 1.0,
    reverbBias: 0,
    bowHold: [3, 6],
    octaveShift: 0,
    motion: { speed: 1.0, amp: 1.0 },
  },
  {
    id: 'abyssal-drones',
    name: 'Abyssal Drones',
    blurb: 'low, bowed, slow-evolving deep',
    // Higher onset rate + long holds so multiple bows OVERLAP into a sustained drone bed
    // (a true drone is many overlapping sustained voices, not sparse isolated tones).
    dials: { calmActive: 0.5, darkBright: 0.12, pureRough: 0.3, roaming: 0.3 },
    mood: 'abyssal',
    excitation: { strike: 0.1, bow: 0.9, grain: 0 },
    decayScale: 1.8,
    reverbBias: 0.3,
    bowHold: [10, 18],
    octaveShift: -1,
    motion: { speed: 0.4, amp: 1.6 }, // slow, wide, languid swells
  },
  {
    id: 'storm-glass',
    name: 'Storm Glass',
    blurb: 'dense, sharp, churning',
    dials: { calmActive: 0.92, darkBright: 0.7, pureRough: 0.78, roaming: 0.6 },
    mood: 'storm',
    excitation: { strike: 0.82, bow: 0.18, grain: 0 },
    decayScale: 0.55,
    reverbBias: -0.1,
    bowHold: [2, 4],
    octaveShift: 0,
    motion: { speed: 2.2, amp: 2.4 }, // fast, turbulent, lots of collisions
  },
  {
    id: 'ice-garden',
    name: 'Ice Garden',
    blurb: 'sparse, high, pure shimmer',
    dials: { calmActive: 0.25, darkBright: 0.96, pureRough: 0.18, roaming: 0.35 },
    mood: 'tidepool',
    excitation: { strike: 0.25, bow: 0.75, grain: 0 },
    decayScale: 1.4,
    reverbBias: 0.15,
    bowHold: [5, 9],
    octaveShift: 1,
    motion: { speed: 0.25, amp: 0.5 }, // nearly still, frozen
  },
  {
    id: 'tidal-choir',
    name: 'Tidal Choir',
    blurb: 'bowed mid voices, flowing',
    dials: { calmActive: 0.5, darkBright: 0.5, pureRough: 0.4, roaming: 0.5 },
    mood: 'tidepool',
    excitation: { strike: 0.2, bow: 0.8, grain: 0 },
    decayScale: 1.3,
    reverbBias: 0.2,
    bowHold: [4, 8],
    octaveShift: 0,
    motion: { speed: 0.9, amp: 1.5 }, // gentle currents
  },
  {
    id: 'reef',
    name: 'Reef',
    blurb: 'mixed struck + bowed, lively',
    dials: { calmActive: 0.65, darkBright: 0.55, pureRough: 0.5, roaming: 0.55 },
    mood: 'tidepool',
    excitation: { strike: 0.6, bow: 0.4, grain: 0 },
    decayScale: 0.9,
    reverbBias: 0.05,
    bowHold: [3, 6],
    octaveShift: 0,
    motion: { speed: 1.4, amp: 1.8 }, // active, frequent contact
  },
  {
    id: 'current',
    name: 'Current',
    blurb: 'fast drift, percussive collisions',
    dials: { calmActive: 0.8, darkBright: 0.6, pureRough: 0.6, roaming: 0.8 },
    mood: 'storm',
    excitation: { strike: 0.7, bow: 0.3, grain: 0 },
    decayScale: 0.7,
    reverbBias: 0,
    bowHold: [2, 5],
    octaveShift: 0,
    motion: { speed: 1.8, amp: 2.8 }, // strong sweeping motion
  },

  // ---- 10 scenes using scales / sonic bases / rhythm / delay / zones ----
  {
    id: 'bioluminescence',
    name: 'Bioluminescence',
    blurb: 'sparse high glass, twinkles + shimmer echo',
    dials: { calmActive: 0.45, darkBright: 0.95, pureRough: 0.2, roaming: 0.5 },
    mood: 'tidepool',
    excitation: { strike: 0.85, bow: 0.15, grain: 0 },
    decayScale: 1.1,
    reverbBias: 0.2,
    bowHold: [3, 6],
    octaveShift: 1,
    motion: { speed: 0.6, amp: 1.0 },
    scale: 'pentatonic',
    ratioFamily: 'glass',
    multihit: { pattern: 'flam' },
    delay: { wet: 0.5, timeSec: 0.38, feedback: 0.45 },
    zoning: 0.3,
  },
  {
    id: 'tidal-pulse',
    name: 'Tidal Pulse',
    blurb: 'rhythmic euclidean pulse, mid, struck',
    dials: { calmActive: 0.7, darkBright: 0.55, pureRough: 0.4, roaming: 0.5 },
    mood: 'tidepool',
    excitation: { strike: 0.9, bow: 0.1, grain: 0 },
    decayScale: 0.7,
    reverbBias: 0,
    bowHold: [2, 4],
    octaveShift: 0,
    motion: { speed: 1.2, amp: 1.4 },
    scale: 'dorian',
    multihit: { pattern: 'euclidean', count: 3, steps: 8, spreadSec: 0.7 },
    delay: { wet: 0.3, timeSec: 0.35, feedback: 0.4 },
  },
  {
    id: 'leviathan',
    name: 'Leviathan',
    blurb: 'sub-bass bowed drones, vast',
    dials: { calmActive: 0.52, darkBright: 0.05, pureRough: 0.25, roaming: 0.2 },
    mood: 'abyssal',
    excitation: { strike: 0.05, bow: 0.95, grain: 0 },
    decayScale: 2.2,
    reverbBias: 0.4,
    bowHold: [14, 24],
    octaveShift: -2,
    motion: { speed: 0.3, amp: 1.2 },
    scale: 'fifths',
    ratioFamily: 'sub',
  },
  {
    id: 'coral-bloom',
    name: 'Coral Bloom',
    blurb: 'dense bowed organ chords, lush',
    dials: { calmActive: 0.55, darkBright: 0.6, pureRough: 0.3, roaming: 0.5 },
    mood: 'tidepool',
    excitation: { strike: 0.2, bow: 0.8, grain: 0 },
    decayScale: 1.4,
    reverbBias: 0.25,
    bowHold: [8, 14],
    octaveShift: 0,
    motion: { speed: 0.7, amp: 1.3 },
    scale: 'just',
    ratioFamily: 'organ',
  },
  {
    id: 'gamelan-reef',
    name: 'Gamelan Reef',
    blurb: 'metallic gamelan ratios, shimmering',
    dials: { calmActive: 0.6, darkBright: 0.6, pureRough: 0.45, roaming: 0.5 },
    mood: 'tidepool',
    excitation: { strike: 0.85, bow: 0.15, grain: 0 },
    decayScale: 1.0,
    reverbBias: 0.1,
    bowHold: [3, 6],
    octaveShift: 0,
    motion: { speed: 1.0, amp: 1.4 },
    scale: 'pentatonic',
    ratioFamily: 'gamelan',
    multihit: { pattern: 'double', spreadSec: 0.18 },
  },
  {
    id: 'frost',
    name: 'Frost',
    blurb: 'microtonal high glass, long silences',
    dials: { calmActive: 0.4, darkBright: 0.95, pureRough: 0.15, roaming: 0.3 },
    mood: 'tidepool',
    excitation: { strike: 0.3, bow: 0.7, grain: 0 },
    decayScale: 1.5,
    reverbBias: 0.2,
    bowHold: [6, 12],
    octaveShift: 1,
    motion: { speed: 0.25, amp: 0.5 },
    scale: 'microtonal',
    ratioFamily: 'glass',
  },
  {
    id: 'sonar',
    name: 'Sonar',
    blurb: 'lone pings, long echo returns',
    dials: { calmActive: 0.4, darkBright: 0.4, pureRough: 0.3, roaming: 0.3 },
    mood: 'abyssal',
    excitation: { strike: 0.9, bow: 0.1, grain: 0 },
    decayScale: 0.8,
    reverbBias: 0.3,
    bowHold: [3, 6],
    octaveShift: -1,
    motion: { speed: 0.5, amp: 1.0 },
    scale: 'fifths',
    delay: { wet: 0.6, timeSec: 0.6, feedback: 0.55 },
  },
  {
    id: 'brine',
    name: 'Brine',
    blurb: 'gritty, rough, dense, dry',
    dials: { calmActive: 0.75, darkBright: 0.45, pureRough: 0.85, roaming: 0.6 },
    mood: 'storm',
    excitation: { strike: 0.7, bow: 0.3, grain: 0 },
    decayScale: 0.5,
    reverbBias: -0.15,
    bowHold: [2, 4],
    octaveShift: 0,
    motion: { speed: 1.6, amp: 2.0 },
    scale: 'chromatic',
    ratioFamily: 'fm',
    multihit: { pattern: 'flam' },
  },
  {
    id: 'maelstrom',
    name: 'Maelstrom',
    blurb: 'chaotic rolls, wide, churning',
    dials: { calmActive: 0.95, darkBright: 0.65, pureRough: 0.7, roaming: 0.9 },
    mood: 'storm',
    excitation: { strike: 0.8, bow: 0.2, grain: 0 },
    decayScale: 0.6,
    reverbBias: 0.1,
    bowHold: [2, 4],
    octaveShift: 0,
    motion: { speed: 2.4, amp: 2.8 },
    scale: 'chromatic',
    multihit: { pattern: 'roll', count: 4, spreadSec: 0.35 },
    zoning: 0.4,
    gravity: true, // churning scene — tilt to slosh, shake to scatter
  },
  {
    id: 'aurora',
    name: 'Aurora',
    blurb: 'whole-tone bowed pads, dreamy',
    dials: { calmActive: 0.52, darkBright: 0.7, pureRough: 0.35, roaming: 0.6 },
    mood: 'tidepool',
    excitation: { strike: 0.25, bow: 0.75, grain: 0 },
    decayScale: 1.4,
    reverbBias: 0.3,
    bowHold: [7, 13],
    octaveShift: 0,
    motion: { speed: 0.6, amp: 1.2 },
    scale: 'wholeTone',
    ratioFamily: 'glass',
    delay: { wet: 0.35, timeSec: 0.5, feedback: 0.4 },
    zoning: 0.2,
  },
  {
    id: 'plankton-bloom',
    name: 'Plankton Bloom',
    blurb: 'ocean grains ring the shells',
    // Grain-forward: most events are granular strikes (slices of the selected ocean recording).
    // With Excite=None they fall back to noise strikes, so it stays playable either way.
    dials: { calmActive: 0.5, darkBright: 0.62, pureRough: 0.45, roaming: 0.55 },
    mood: 'tidepool',
    excitation: { strike: 0.2, bow: 0, grain: 0.8 },
    decayScale: 1.1,
    reverbBias: 0.2,
    bowHold: [3, 6],
    octaveShift: 0,
    motion: { speed: 0.8, amp: 1.2 },
    scale: 'pentatonic',
    ratioFamily: 'glass',
    delay: { wet: 0.25, timeSec: 0.42, feedback: 0.35 },
    excitationBank: 'surf', // grain-forward — auto-pick the always-available synth surf bank
  },
  {
    id: 'sampled-tide',
    name: 'Sampled Tide',
    blurb: 'plays the ocean recording directly',
    // Sampler-forward: most events trigger a one-shot slice of the selected recording (raw
    // playback through the reverb, not the resonators). Falls back to clean strikes with Excite=None.
    dials: { calmActive: 0.5, darkBright: 0.5, pureRough: 0.4, roaming: 0.5 },
    mood: 'tidepool',
    excitation: { strike: 0.2, bow: 0, grain: 0, sampler: 0.8 },
    decayScale: 1.0,
    reverbBias: 0.15,
    bowHold: [3, 6],
    octaveShift: 0,
    motion: { speed: 0.9, amp: 1.1 },
    scale: 'pentatonic',
    delay: { wet: 0.2, timeSec: 0.38, feedback: 0.3 },
    excitationBank: 'surf', // sampler-forward — auto-pick a bank so it plays a recording
  },
  {
    id: 'tidewrack-drift',
    name: 'Tidewrack Drift',
    blurb: 'bowed ocean drone — a grain cloud',
    // Bow-heavy + overlapping (calmActive >= 0.45 so bows form a bed, not isolated tones). With a
    // Sound bank active, each sustained voice is excited by a continuous grain cloud (an ocean
    // drone); with Excite=None it's the usual filtered-noise bow.
    dials: { calmActive: 0.55, darkBright: 0.2, pureRough: 0.4, roaming: 0.4 },
    mood: 'abyssal',
    excitation: { strike: 0.1, bow: 0.9, grain: 0 },
    decayScale: 1.7,
    reverbBias: 0.35,
    bowHold: [9, 16],
    octaveShift: -1,
    motion: { speed: 0.45, amp: 1.5 },
    scale: 'dorian',
    ratioFamily: 'organ',
    delay: { wet: 0.3, timeSec: 0.6, feedback: 0.45 },
    gravity: true, // a slow drone scene that suits "pour the diatoms" tilt on mobile
    excitationBank: 'surf', // bowed grain cloud — auto-pick a bank so the cloud has content
  },
];

export const DEFAULT_PRESET = PRESETS[0];

/** Weighted pick of an excitation type for a strike under the given preset. */
export function pickExcitation(
  preset: Preset,
  rand: number,
): 'strike' | 'bow' | 'grain' | 'sampler' {
  const { strike, bow, grain } = preset.excitation;
  const sampler = preset.excitation.sampler ?? 0;
  const total = strike + bow + grain + sampler || 1;
  let r = rand * total;
  if ((r -= strike) < 0) return 'strike';
  if ((r -= bow) < 0) return 'bow';
  if ((r -= grain) < 0) return 'grain';
  return sampler > 0 ? 'sampler' : 'strike';
}
